import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import {
  deleteAttachment,
  getGallery,
  saveGallery,
} from "../../../../redux/slices/appSlice";
import { Close } from "@mui/icons-material";
import { attachment_URL } from "../../../../Constants";

export default function GalleryUpload({
  galleryDialogOpen,
  setGalleryDialogOpen,
  deleteTitle,
  fk_sectionDataId,
  companyId,
}) {
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (galleryDialogOpen && fk_sectionDataId) {
      setFiles([]);
      setAttachments([]);
      dispatch(getGallery(fk_sectionDataId)).then((result) => {
        if (result.payload?.status === "success") {
          setAttachments(result.payload.attachments || []);
        }
      });
    }
  }, [dispatch, fk_sectionDataId, galleryDialogOpen]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/gif": [],
      "image/webp": [],
      "image/svg+xml": [],
    },
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );
      setFiles((prev) => [...prev, ...newFiles]);
    },
  });

  const handleConfirmDelete = (id) => {
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleRemoveAttachment = async () => {
    if (!deleteId) return;
    try {
      // Optimistic update: Remove the item from state before confirming deletion
      setAttachments((prevAttachments) =>
        prevAttachments.filter((attachment) => attachment.id !== deleteId)
      );

      const result = await dispatch(deleteAttachment(deleteId));

      if (result?.status === "success") {
        // Fetch updated gallery immediately after deletion
        dispatch(getGallery(fk_sectionDataId)).then((updatedGallery) => {
          setAttachments(updatedGallery.payload.attachments || []);
        });
      } else {
        console.error("Delete failed:", result.error);
      }
    } catch (error) {
    } finally {
      setConfirmDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleUpload = async () => {
    if (!files.length && !attachments.length) {
      console.error("No files selected for upload.");
      return;
    }

    const allFiles = [
      ...files,
      ...attachments.map((attachment) => ({
        name: attachment.file_name,
        existing: true,
      })),
    ];

    try {
      const result = await dispatch(
        saveGallery({ fk_sectionDataId, files: allFiles })
      );
      if (result?.status === "success") {
        setFiles([]);
        const updatedGallery = await dispatch(getGallery(fk_sectionDataId));
        setAttachments(updatedGallery.payload.attachments || []);
      }
      setGalleryDialogOpen(false);
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  const getImageSrc = (fileName) => {
    return encodeURI(
      `${attachment_URL}/${companyId}/sectionData/${fk_sectionDataId}/gallery/${fileName}`
    );
  };

  return (
    <>
      <Dialog
        open={galleryDialogOpen}
        onClose={() => setGalleryDialogOpen(false)}
      >
        <DialogTitle>
          Upload Gallery for <strong>{deleteTitle}</strong>
        </DialogTitle>
        <DialogContent>
          <div
            {...getRootProps()}
            style={{
              border: "2px dashed #ccc",
              padding: 20,
              textAlign: "center",
              cursor: "pointer",
              marginTop: 10,
            }}
          >
            <input {...getInputProps()} />
            <p>Drag & drop files here, or click to select</p>
          </div>

          {files.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginTop: 10,
              }}
            >
              {files.map((file, index) => (
                <div key={`upload-${index}`} style={{ position: "relative" }}>
                  {file.type.startsWith("image/") ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 5,
                      }}
                    />
                  ) : (
                    <p>{file.name}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <Divider style={{ margin: "15px 0" }} />

          {attachments.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginTop: 10,
              }}
            >
              {attachments.map((attachment) => (
                <div key={attachment.id} style={{ position: "relative" }}>
                  <img
                    src={getImageSrc(attachment.file_name)}
                    alt={attachment.file_name}
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 5,
                    }}
                  />
                  <IconButton
                    onClick={() => handleConfirmDelete(attachment.id)}
                    size="small"
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      background: "rgba(255, 255, 255, 0.7)",
                      borderRadius: "50%",
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGalleryDialogOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={handleUpload}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this attachment?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleRemoveAttachment}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
